import axios from 'axios';
import { getConfig } from '../config';
import { AdminApiDomain, getAdminApiUrl } from '../config/ApiRouting';

export type QuestionnaireFormType = 'survey' | 'skin_quiz' | 'campaign_survey' | 'nps';

export type QuestionnaireStatus = 'draft' | 'published' | 'archived';

export type QuestionnaireAnalysisStatus = 'pending' | 'forwarded' | 'failed';

export type QuestionnaireAnswerValue = string | string[] | number | boolean;

export interface QuestionnaireOption {
  value: string;
  label: string;
}

export interface QuestionnaireQuestion {
  questionId: string;
  type?: string;
  label?: string;
  options?: QuestionnaireOption[];
}

export interface QuestionnaireSchema {
  questions: QuestionnaireQuestion[];
}

export interface QuestionnaireVersion {
  id: number;
  version: number;
  status: QuestionnaireStatus;
  schema: QuestionnaireSchema;
  publishedAt: string | null;
  createdAt: string;
}

export interface Questionnaire {
  id: number;
  surveyUuid: string;
  title: string;
  formType: QuestionnaireFormType;
  status: QuestionnaireStatus;
  latestVersion: QuestionnaireVersion | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  responseCount?: number;
}

export interface QuestionnaireAnswer {
  questionId: string;
  label?: string;
  answer: QuestionnaireAnswerValue;
}

export interface QuestionnaireResponse {
  id: number;
  questionnaireId: number;
  versionId: number;
  actorId: string | null;
  userId: number | null;
  eventId: string;
  source: string;
  language: string | null;
  answers: QuestionnaireAnswer[];
  submittedAt: string;
  analysisStatus: QuestionnaireAnalysisStatus;
  analysisError: string | null;
}

export interface SaveQuestionnaireRequest {
  surveyUuid: string;
  title: string;
  formType: QuestionnaireFormType;
  schema: QuestionnaireSchema;
  createdBy?: string;
}

export interface UpdateQuestionnaireDraftRequest {
  title?: string;
  formType?: QuestionnaireFormType;
  schema: QuestionnaireSchema;
}

export interface SubmitQuestionnaireResponseRequest {
  versionId: number;
  actorId?: string;
  source: string;
  language?: string;
  answers: QuestionnaireAnswer[];
}

export class QuestionnaireClient {
  private readonly baseUrl: string;
  private readonly headers: Record<string, string>;

  constructor() {
    this.baseUrl = getAdminApiUrl(AdminApiDomain.QUESTIONNAIRE);
    this.headers = {
      'X-API-KEY': getConfig().apiKey,
    };
  }

  public async listQuestionnaires(): Promise<Questionnaire[]> {
    const response = await axios.get<Questionnaire[]>(`${this.baseUrl}/admin/questionnaires`, {
      headers: this.headers,
    });
    return response.data;
  }

  public async createQuestionnaire(request: SaveQuestionnaireRequest): Promise<Questionnaire> {
    const response = await axios.post<Questionnaire>(`${this.baseUrl}/admin/questionnaires`, request, {
      headers: this.headers,
    });
    return response.data;
  }

  public async updateDraft(questionnaireId: number, request: UpdateQuestionnaireDraftRequest): Promise<Questionnaire> {
    const response = await axios.put<Questionnaire>(
      `${this.baseUrl}/admin/questionnaires/${questionnaireId}/draft`,
      request,
      {
        headers: this.headers,
      },
    );
    return response.data;
  }

  public async publish(questionnaireId: number): Promise<Questionnaire> {
    const response = await axios.post<Questionnaire>(
      `${this.baseUrl}/admin/questionnaires/${questionnaireId}/publish`,
      {},
      { headers: this.headers },
    );
    return response.data;
  }

  public async deployVersion(
    questionnaireId: number,
    request: UpdateQuestionnaireDraftRequest,
  ): Promise<Questionnaire> {
    const response = await axios.post<Questionnaire>(
      `${this.baseUrl}/admin/questionnaires/${questionnaireId}/deploy`,
      request,
      {
        headers: this.headers,
      },
    );
    return response.data;
  }

  public async listResponses(questionnaireId: number): Promise<QuestionnaireResponse[]> {
    const response = await axios.get<QuestionnaireResponse[]>(
      `${this.baseUrl}/admin/questionnaires/${questionnaireId}/responses`,
      {
        headers: this.headers,
      },
    );
    return response.data;
  }

  public async submitResponse(
    surveyUuid: string,
    request: SubmitQuestionnaireResponseRequest,
  ): Promise<QuestionnaireResponse> {
    const response = await axios.post<QuestionnaireResponse>(
      `${this.baseUrl}/questionnaires/${surveyUuid}/responses`,
      request,
      {
        headers: this.headers,
      },
    );
    return response.data;
  }
}
